import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, Button, Card, CardContent, Grid, Fab, Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton, Menu, MenuItem } from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  MoreVert,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { callFunction } from "../firebase";
import { UserContext } from "./AuthGuard";
import DOMPurify from "dompurify";

const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, post: null });
  const [menuAnchor, setMenuAnchor] = useState({});
  const [user] = useContext(UserContext);
  const navigate = useNavigate();
  // Backend-driven list (via Cloud Functions)

  useEffect(() => {
    let isCancelled = false;
    const toDate = (ts) => {
      if (!ts) return undefined;
      if (ts instanceof Date) return ts;
      if (typeof ts === "number") return new Date(ts);
      const seconds = ts.seconds ?? ts._seconds;
      const nanos = ts.nanoseconds ?? ts._nanoseconds ?? 0;
      if (typeof seconds === "number") {
        return new Date(seconds * 1000 + Math.floor(nanos / 1e6));
      }
      return undefined;
    };
    async function loadPosts() {
      try {
        setLoading(true);
        const response = await callFunction("posts", {}, "GET");
        if (!isCancelled) {
          const postsData = (response?.posts || []).map((p) => ({
            ...p,
            createdAt: toDate(p.createdAt),
            updatedAt: toDate(p.updatedAt),
          }));
          setPosts(postsData);
        }
      } catch (e) {
        console.error("Failed to load posts:", e);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    }
    loadPosts();
    return () => {
      isCancelled = true;
    };
  }, []);

  const handleMenuOpen = (event, postId) => {
    setMenuAnchor({ ...menuAnchor, [postId]: event.currentTarget });
  };

  const handleMenuClose = (postId) => {
    setMenuAnchor({ ...menuAnchor, [postId]: null });
  };

  const handleDeleteClick = (post) => {
    setDeleteDialog({ open: true, post });
    handleMenuClose(post.id);
  };

  const handleDeleteConfirm = async () => {
    try {
      await callFunction(`posts/${deleteDialog.post.id}`, {}, "DELETE");
      setDeleteDialog({ open: false, post: null });
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const canEditPost = (post) => {
    return user?.id === post.authorId || user?.role === "admin";
  };

  const renderMarkdown = (content) => {
    // Simple markdown-like rendering for demo purposes
    const html = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');
    
    return { __html: DOMPurify.sanitize(html) };
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography>Loading posts...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Posts
        </Typography>
        <Button
          component={RouterLink}
          to="/posts/new"
          variant="contained"
          startIcon={<Add />}
        >
          New Post
        </Button>
      </Box>

      {posts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No posts yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Be the first to create a post and start the conversation!
            </Typography>
            <Button
              component={RouterLink}
              to="/posts/new"
              variant="contained"
              startIcon={<Add />}
            >
              Create First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {posts.map((post) => (
            <Grid item xs={12} key={post.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {post.title}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Chip
                          label={post.authorName}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {post.createdAt?.toLocaleDateString()} at {post.createdAt?.toLocaleTimeString()}
                        </Typography>
                        {post.updatedAt && post.updatedAt > post.createdAt && (
                          <Chip label="Edited" size="small" color="secondary" />
                        )}
                      </Box>
                    </Box>
                    {canEditPost(post) && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, post.id)}
                      >
                        <MoreVert />
                      </IconButton>
                    )}
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{ mb: 2 }}
                    dangerouslySetInnerHTML={renderMarkdown(post.content)}
                  />
                </CardContent>

                {/* Post Menu */}
                <Menu
                  anchorEl={menuAnchor[post.id]}
                  open={Boolean(menuAnchor[post.id])}
                  onClose={() => handleMenuClose(post.id)}
                >
                  <MenuItem
                    onClick={() => {
                      navigate(`/posts/edit/${post.id}`);
                      handleMenuClose(post.id);
                    }}
                  >
                    <Edit sx={{ mr: 1 }} fontSize="small" />
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleDeleteClick(post)}
                    sx={{ color: "error.main" }}
                  >
                    <Delete sx={{ mr: 1 }} fontSize="small" />
                    Delete
                  </MenuItem>
                </Menu>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add post"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        component={RouterLink}
        to="/posts/new"
      >
        <Add />
      </Fab>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, post: null })}
      >
        <DialogTitle>Delete Post</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.post?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, post: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Posts; 